import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { API_BASE_URL } from '../config';

interface ReportEntry {
  date: string;
  size: number;
  last_modified: string;
}

interface HealthCheckData {
  status: string;
  timestamp: string;
  webhook_health: {
    system_status: string;
    heartbeats: Array<{
      esp_id: string;
      geo: string;
      provider: string;
      events_24h: number;
      hours_ago: number;
      is_healthy: boolean;
    }>;
  };
  seed_health: {
    total_seeds: number;
    all_healthy: boolean;
    seeds: Array<{
      email: string;
      isp: string;
      coverage: string;
      is_healthy: boolean;
    }>;
  };
  secondary_esps: Array<{
    esp_id: string;
    label: string;
    geo: string;
    provider: string;
    ready_send: number;
    capacity_pct: number;
    open_rate_7d: number;
    bounce_rate_7d: number;
    spam_rate_7d: number;
    recommended_quota: string;
    status: string;
    recommendation_note: string;
  }>;
  preflight_checks: {
    campaigns_checked: number;
    total_warnings: number;
    items: Array<{
      campaign: string;
      esp: string;
      geo: string;
      scheduled: string;
      expected_domain: string;
      check_status: string;
      warnings: string[];
    }>;
  };
}

export default function DailyReports() {
  const [reports, setReports] = useState<ReportEntry[]>([]);
  const [selectedDate, setSelectedDate] = useState('latest');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Live Health Check State
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [healthData, setHealthData] = useState<HealthCheckData | null>(null);
  const [showHealthModal, setShowHealthModal] = useState(false);

  // SimplyLoan SEO Content Buffer Tracking
  const [simplyloanBuffer, setSimplyloanBuffer] = useState<{ count: number; days: number; isLow: boolean }>({
    count: 3,
    days: 10,
    isLow: false
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem("simplyloan_seo_content_v1");
      if (saved) {
        const items = JSON.parse(saved);
        const ready = items.filter((c: any) => c.status === "ready_buffer" || c.status === "scheduled").length;
        setSimplyloanBuffer({
          count: ready,
          days: Math.round(ready * 3.5),
          isLow: ready < 3
        });
      }
    } catch {
      // ignore
    }
  }, []);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Load list of available reports
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/reports/daily`);
      const data = await res.json();
      setReports(data.reports || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load a specific report's HTML
  const loadReport = useCallback(async (date: string) => {
    setLoadingReport(true);
    setError('');
    setSuccessMsg('');
    setSelectedDate(date);
    try {
      const res = await fetch(`${API_BASE_URL}/api/reports/daily?date=${date}`);
      const data = await res.json();
      if (data.error) {
        setError(data.error);
        setHtml('');
      } else {
        setHtml(data.html || '');
      }
    } catch (err: any) {
      setError(err.message);
      setHtml('');
    } finally {
      setLoadingReport(false);
    }
  }, []);

  // Run on-demand Health Check
  const runLiveHealthCheck = async () => {
    setCheckingHealth(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/allocation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'run_health_check' })
      });
      const data = await res.json();
      const payload = typeof data.body === 'string' ? JSON.parse(data.body) : (data.body || data);
      setHealthData(payload);
      setShowHealthModal(true);
    } catch (err: any) {
      setError('Health check failed: ' + err.message);
    } finally {
      setCheckingHealth(false);
    }
  };

  // Generate a fresh report on-demand
  const triggerGenerateReport = async () => {
    if (!window.confirm("Generate a fresh daily report and email digest now?")) return;
    setGeneratingReport(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/allocation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'generate_daily_report' })
      });
      const data = await res.json();
      setSuccessMsg("Fresh report generated successfully and emailed to kristoffer@afima.dk!");
      await loadList();
      await loadReport('latest');
    } catch (err: any) {
      setError('Failed to generate report: ' + err.message);
    } finally {
      setGeneratingReport(false);
    }
  };

  useEffect(() => {
    loadList();
    loadReport('latest');
  }, [loadList, loadReport]);

  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in pb-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div>
            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 flex items-center gap-2">
              📊 Operational Intelligence & Health Suite
            </h2>
            <p className="text-gray-500 mt-1 text-sm">
              Comprehensive telemetry, affiliate commissions, 30-day cohorts, ISP deliverability, and pre-flight health checks
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Live Health Check Trigger */}
            <button
              onClick={runLiveHealthCheck}
              disabled={checkingHealth}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium text-sm rounded-xl hover:from-purple-700 hover:to-indigo-700 transition shadow-sm disabled:opacity-50"
            >
              {checkingHealth ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>Auditing...</span>
                </>
              ) : (
                <>
                  <span>🩺</span>
                  <span>Run Live Health Audit</span>
                </>
              )}
            </button>

            {/* Generate Fresh Report Trigger */}
            <button
              onClick={triggerGenerateReport}
              disabled={generatingReport}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium text-sm rounded-xl hover:bg-emerald-700 transition shadow-sm disabled:opacity-50"
            >
              {generatingReport ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>Generate Today's Report</span>
                </>
              )}
            </button>

            {/* Report date selector */}
            <select
              value={selectedDate}
              onChange={(e) => loadReport(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-700 text-sm rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm min-w-[200px]"
            >
              <option value="latest">📌 Latest Report</option>
              {reports.map((r) => (
                <option key={r.date} value={r.date}>
                  {formatDate(r.date)} ({formatSize(r.size)})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl border border-red-200 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 font-bold">✕</button>
          </div>
        )}
        {successMsg && (
          <div className="bg-emerald-50 text-emerald-800 p-4 rounded-xl border border-emerald-200 text-sm flex items-center justify-between">
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg('')} className="text-emerald-500 hover:text-emerald-700 font-bold">✕</button>
          </div>
        )}

        {/* SimplyLoan Content Buffer Health Strip */}
        <div className={`p-4 rounded-2xl border flex flex-wrap items-center justify-between gap-4 transition shadow-xs ${
          simplyloanBuffer.isLow 
            ? 'bg-amber-50/90 border-amber-200 text-amber-950' 
            : 'bg-gradient-to-r from-emerald-50/60 to-teal-50/60 border-emerald-200 text-emerald-950'
        }`}>
          <div className="flex items-center gap-3.5">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 ${
              simplyloanBuffer.isLow ? 'bg-amber-200/80 text-amber-800' : 'bg-emerald-200/80 text-emerald-800'
            }`}>
              {simplyloanBuffer.isLow ? '⚠️' : '🚀'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-bold text-gray-900">SimplyLoan.se Content Drip Pipeline</h4>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  simplyloanBuffer.isLow ? 'bg-amber-200 text-amber-900 border border-amber-300' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                }`}>
                  {simplyloanBuffer.isLow ? 'Low Buffer Warning' : 'Buffer Optimal'}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-0.5">
                <span className="font-semibold text-gray-800">{simplyloanBuffer.count} artikler klar i reserve</span> (~{simplyloanBuffer.days} dages køreplan ved 2 udgivelser/uge). 
                {simplyloanBuffer.isLow ? ' ⚠️ Advarsel: Buffertanken er under 3 artikler! Opret og godkend nye udkast for at forhindre at crawleren sulter.' : ' Pipeline kører med automatisk tidsmæssig jitter.'}
              </p>
            </div>
          </div>
          <Link
            to="/simplyloan/seo"
            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-200 hover:border-indigo-400 text-indigo-700 font-semibold text-xs rounded-xl transition shadow-xs"
          >
            <span>Gå til SEO & Content Hub</span>
            <span>→</span>
          </Link>
        </div>

        {/* Live Health Check Modal / Drawer */}
        {showHealthModal && healthData && (
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 text-slate-100 shadow-xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  🩺 Live Deliverability & Pipeline Diagnostics
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                    healthData.status === 'HEALTHY' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-yellow-950 text-yellow-400 border border-yellow-800'
                  }`}>
                    {healthData.status === 'HEALTHY' ? 'ALL SYSTEMS OPERATIONAL 🟢' : 'ATTENTION REQUIRED 🟡'}
                  </span>
                </h3>
                <p className="text-xs text-slate-400 mt-1">Audit Timestamp: {new Date(healthData.timestamp).toLocaleString()}</p>
              </div>
              <button
                onClick={() => setShowHealthModal(false)}
                className="text-slate-400 hover:text-white px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm"
              >
                Close ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Webhook Heartbeat Card */}
              <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>📡 Webhook Telemetry</span>
                  <span className="text-emerald-400 font-bold">{healthData.webhook_health.system_status}</span>
                </div>
                <div className="space-y-2 mt-3 max-h-48 overflow-y-auto text-xs">
                  {healthData.webhook_health.heartbeats.map((hb) => (
                    <div key={hb.esp_id} className="flex items-center justify-between py-1 border-b border-slate-700/50">
                      <span className="font-mono text-slate-300">{hb.esp_id} ({hb.geo})</span>
                      <span className="text-slate-400">{hb.events_24h.toLocaleString()} evts • {hb.hours_ago}h ago</span>
                      <span>{hb.is_healthy ? '🟢' : '🔴'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Seed Network Card */}
              <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>📬 Seed Accounts Network</span>
                  <span className="text-emerald-400 font-bold">{healthData.seed_health.total_seeds}/5 Inboxes</span>
                </div>
                <div className="space-y-2 mt-3 max-h-48 overflow-y-auto text-xs">
                  {healthData.seed_health.seeds.map((s) => (
                    <div key={s.email} className="flex items-center justify-between py-1 border-b border-slate-700/50">
                      <span className="font-mono text-slate-300 truncate max-w-[150px]">{s.email}</span>
                      <span className="text-slate-400">{s.coverage}</span>
                      <span>{s.is_healthy ? '🟢' : '🟡'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pre-Flight Links Card */}
              <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>🔗 Pre-Flight Link Checks</span>
                  <span className="text-indigo-400 font-bold">{healthData.preflight_checks.campaigns_checked} Audited</span>
                </div>
                <div className="text-xs space-y-2 mt-3 max-h-48 overflow-y-auto">
                  {healthData.preflight_checks.items.length === 0 ? (
                    <p className="text-slate-500 italic">No scheduled campaigns awaiting validation</p>
                  ) : (
                    healthData.preflight_checks.items.map((p, idx) => (
                      <div key={idx} className="py-1 border-b border-slate-700/50">
                        <div className="flex items-center justify-between font-medium text-slate-300">
                          <span className="truncate max-w-[180px]">{p.campaign}</span>
                          <span>{p.check_status === 'PASS' ? '🟢' : '🟡'}</span>
                        </div>
                        <div className="text-[11px] text-slate-400">{p.expected_domain} • {p.scheduled}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Quota Recommendations Table */}
            <div className="border-t border-slate-800 pt-4">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                ⚙️ Self-Regulating Quota Recommendations
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-800">
                      <th className="py-2">Account</th>
                      <th>Market</th>
                      <th className="text-right">Send Segment</th>
                      <th className="text-right">Cap Usage</th>
                      <th className="text-right">7d Open %</th>
                      <th className="text-right">7d Bounce %</th>
                      <th className="text-right">Rec. Quota</th>
                      <th className="text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthData.secondary_esps.map((e) => (
                      <tr key={e.esp_id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                        <td className="py-2 font-medium text-slate-200">{e.label}</td>
                        <td className="text-slate-400">{e.geo} / {e.provider}</td>
                        <td className="text-right text-slate-300 font-mono">{e.ready_send.toLocaleString()}</td>
                        <td className="text-right text-slate-300">{e.capacity_pct}%</td>
                        <td className="text-right text-slate-300">{e.open_rate_7d}%</td>
                        <td className="text-right text-slate-300">{e.bounce_rate_7d}%</td>
                        <td className="text-right font-bold text-emerald-400 font-mono">{e.recommended_quota}</td>
                        <td className="text-right">
                          <span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-emerald-950 text-emerald-400 border border-emerald-800">
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Report Content Iframe */}
        {loadingReport ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center animate-pulse shadow-sm">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-gray-700">Loading Intelligence Report...</h3>
            <p className="text-gray-400 mt-1">Collecting commissions, FeedBlitz cohorts, and deliverability matrices</p>
          </div>
        ) : html ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-700">
                  Viewing: {selectedDate === 'latest' ? '📌 Latest Report' : formatDate(selectedDate)}
                </span>
                <span className="text-xs px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-medium">
                  Complete Intelligence Suite
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const w = window.open('', '_blank');
                    if (w) { w.document.write(html); w.document.close(); }
                  }}
                  className="px-3.5 py-1.5 text-xs font-semibold bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition shadow-sm"
                >
                  🔗 Open in New Tab
                </button>
              </div>
            </div>

            <iframe
              ref={iframeRef}
              srcDoc={html}
              title="Daily ESP Operational Intelligence Report"
              className="w-full border-0"
              style={{ minHeight: '85vh' }}
              sandbox="allow-same-origin allow-scripts"
            />
          </div>
        ) : !error ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-sm">
            <div className="text-5xl mb-4">📭</div>
            <h3 className="text-lg font-semibold text-gray-700">No reports generated yet</h3>
            <p className="text-gray-400 mt-1 mb-4">
              Click the button below to generate today's comprehensive intelligence report.
            </p>
            <button
              onClick={triggerGenerateReport}
              disabled={generatingReport}
              className="px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition"
            >
              Generate First Report
            </button>
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
}
